import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { DashboardWidget } from './DashboardWidget';
import type { NameValueItem } from './types';

type RankingBarChartProps = {
  title: string;
  data: NameValueItem[];
  loading?: boolean;
  valueColor?: string;
  height?: number;
  formatValue?: (value: number) => string;
};

const renderCustomYAxisTick = (props: any) => {
  const {
    x,
    y,
    payload,
  } = props;

  const splitLabelIntoLines = (text: string, maxCharsPerLine = 18) => {
    const words = String(text).split(' ');
    const lines: string[] = [];
    let currentLine = '';

    words.forEach((word) => {
      const nextLine = currentLine ? `${currentLine} ${word}` : word;

      if (nextLine.length <= maxCharsPerLine) {
        currentLine = nextLine;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });

    if (currentLine) lines.push(currentLine);

    return lines;
  };

  // const lines = String(payload.value).split('\n');
  const lines = splitLabelIntoLines(String(payload.value), 18);

  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line: string, index: number) => (
        <text
          key={`${payload.value}-${index}` as string}
          x={0}
          y={index * 12 - ((lines.length - 1) * 6)}
          dy={4}
          textAnchor="end"
          fill="#6b6b6b"
          fontSize={11}
          fontWeight={600}
        >
          {line}
        </text>
      ))}
    </g>
  );
};

export const RankingBarChart = ({
  title,
  data,
  loading = false,
  valueColor = '#42a5f5',
  height = 430,
  formatValue = (value) => `${value}`,
}: RankingBarChartProps) => {
  const maxValue = Math.max(...data.map((item) => item.value), 0);
  const domainMax = maxValue > 0 ? Math.ceil(maxValue * 1.15) : 10;

  const renderValueLabel = (props: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    value?: number;
  }) => {
    const {
      x = 0,
      y = 0,
      width = 0,
      height: barHeight = 0,
      value,
    } = props;

    if (value === undefined || value === null) return null;

    const isInside = width > 50;

    return (
      <text
        x={isInside ? x + width - 6 : x + width + 6}
        y={y + barHeight / 2}
        fill={isInside ? '#ffffff' : valueColor}
        textAnchor={isInside ? 'end' : 'start'}
        dominantBaseline="middle"
        fontSize={12}
        fontWeight={700}
      >
        {formatValue(value)}
      </text>
    );
  };

  return (
    <DashboardWidget title={title} loading={loading} hasData={data.length > 0}>
      <div style={{ width: '100%', height: height - 90 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{
              top: 0,
              right: 35,
              left: 0,
              bottom: 0,
            }}
            barCategoryGap="18%"
          >
            <CartesianGrid stroke="#e3e3e3" horizontal={false} />

            <XAxis
              type="number"
              domain={[0, domainMax]}
              tick={{ fontSize: 12, fill: '#6b6b6b' }}
              axisLine={false}
              tickLine={false}
            />

            <YAxis
              type="category"
              dataKey="name"
              width={140}
              tickLine={false}
              axisLine={false}
              tick={renderCustomYAxisTick}
            />

            <Bar dataKey="value" fill={valueColor} barSize={20}>
              {data.map((entry, index) => (
                <Cell key={`${entry.name}-${index}` as string} fill={valueColor} />
              ))}
              <LabelList dataKey="value" content={renderValueLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </DashboardWidget>
  );
};
